import { persistLoginQuestionnaireFields } from '../services/auth';
import {
  persistMedicalQuestionnaireCompletedOnClient,
  saveMedicalQuestionnaireStepAnswer,
} from '../services/medicalQuestionnaire';
import { fetchMyProfile } from '../services/profile';
import {
  clearOptimisticQuestionnaireProgress,
  setOptimisticQuestionnaireProgress,
} from '../store/medicalQuestionnaireProgressSlice';
import { setProfile } from '../store/userSlice';
import { countFilledQuestionnaireSections } from './medicalQuestionnaireStepValidation';

/** Derive live % / section counts from current answers (questionnaire flow only). */
export function computeLocalQuestionnaireProgress(sections, answers, completedSteps = {}) {
  const { filledSections, totalSections } = countFilledQuestionnaireSections(
    sections,
    answers,
    completedSteps
  );
  const optimisticCompletionPercentage =
    totalSections > 0 ? Math.round((filledSections / totalSections) * 100) : 0;
  return { filledSections, totalSections, optimisticCompletionPercentage };
}

export function syncOptimisticQuestionnaireProgress(
  dispatch,
  sections,
  answers,
  completedSteps = {}
) {
  const progress = computeLocalQuestionnaireProgress(sections, answers, completedSteps);
  if (progress.totalSections <= 0) return progress;
  dispatch(
    setOptimisticQuestionnaireProgress({
      optimisticCompletionPercentage: progress.optimisticCompletionPercentage,
      optimisticFilledSections: progress.filledSections,
      totalSections: progress.totalSections,
    })
  );
  return progress;
}

/** Apply banner + questionnaire fields from API (login, save, or profile/me). */
export function applyQuestionnaireFieldsFromSave(dispatch, fields, { forceComplete = false } = {}) {
  const payload = {};
  if (fields?.questionnaire_completion_percentage != null) {
    payload.questionnaire_completion_percentage = fields.questionnaire_completion_percentage;
  }
  if (
    fields?.number_of_questions_filled != null &&
    Number.isFinite(Number(fields.number_of_questions_filled))
  ) {
    payload.number_of_questions_filled = Number(fields.number_of_questions_filled);
  }
  if (typeof fields?.medical_questionnaire_completed === 'boolean') {
    payload.medical_questionnaire_completed = fields.medical_questionnaire_completed;
  }
  if (fields?.section_progress != null && Number.isFinite(Number(fields.section_progress))) {
    payload.section_progress = Math.min(
      100,
      Math.max(0, Math.round(Number(fields.section_progress)))
    );
  }
  if (fields?.sections_completed != null && Number.isFinite(Number(fields.sections_completed))) {
    payload.sections_completed = Math.max(0, Math.round(Number(fields.sections_completed)));
  }
  if (fields?.total_sections != null && Number.isFinite(Number(fields.total_sections))) {
    payload.totalSections = Math.max(0, Math.round(Number(fields.total_sections)));
  } else if (fields?.totalSections != null && Number.isFinite(Number(fields.totalSections))) {
    payload.totalSections = Math.max(0, Math.round(Number(fields.totalSections)));
  }
  if (forceComplete) {
    payload.medical_questionnaire_completed = true;
    payload.questionnaire_completion_percentage = 100;
    payload.section_progress = 100;
  }
  if (Object.keys(payload).length > 0) {
    dispatch(setProfile(payload));
  }

  const persistPayload = {
    medical_questionnaire_completed: payload.medical_questionnaire_completed,
    number_of_questions_filled: payload.number_of_questions_filled,
    questionnaire_completion_percentage: payload.questionnaire_completion_percentage,
    sections_completed: payload.sections_completed,
    section_progress: payload.section_progress,
    total_sections: payload.totalSections,
  };
  if (Object.values(persistPayload).some((v) => v != null)) {
    persistLoginQuestionnaireFields(persistPayload);
  }

  if (payload.section_progress != null) {
    dispatch(
      setOptimisticQuestionnaireProgress({
        optimisticCompletionPercentage: payload.section_progress,
        optimisticFilledSections: payload.sections_completed ?? undefined,
      })
    );
  } else if (payload.questionnaire_completion_percentage != null) {
    dispatch(
      setOptimisticQuestionnaireProgress({
        optimisticCompletionPercentage: payload.questionnaire_completion_percentage,
      })
    );
  }
}

export function profileToQuestionnaireFields(profile) {
  if (!profile) return null;
  const sectionProgress =
    profile.section_progress != null
      ? profile.section_progress
      : profile.completedSectionsPercentage;
  const sectionsCompleted =
    profile.sections_completed != null
      ? profile.sections_completed
      : profile.totalCompletedSections;
  return {
    medical_questionnaire_completed: profile.medical_questionnaire_completed,
    number_of_questions_filled: profile.number_of_questions_filled,
    questionnaire_completion_percentage: profile.questionnaire_completion_percentage,
    section_progress: sectionProgress,
    sections_completed: sectionsCompleted,
    total_sections: profile.totalSections,
  };
}

/** Push latest banner fields into Redux + localStorage (login, save, profile/me). */
export function applyBannerProgressFromProfile(dispatch, profile) {
  const fields = profileToQuestionnaireFields(profile);
  if (fields) {
    applyQuestionnaireFieldsFromSave(dispatch, fields);
  }
  if (!profile) return;
  dispatch(
    setProfile({
      totalSections: profile.totalSections,
      totalCompletedSections: profile.totalCompletedSections,
      totalIncompleteSections: profile.totalIncompleteSections,
      completedSectionsPercentage: profile.completedSectionsPercentage,
      overallStatus: profile.overallStatus,
      section_progress: fields?.section_progress ?? profile.section_progress,
      sections_completed: fields?.sections_completed ?? profile.sections_completed,
    })
  );
}

/** After save: apply response fields, then refresh banner fields from /profile/me. */
export async function applyQuestionnaireProgressAfterSave(
  dispatch,
  fields,
  { forceComplete = false, refetchProfile = true } = {}
) {
  applyQuestionnaireFieldsFromSave(dispatch, fields, { forceComplete });

  if (!refetchProfile) {
    return fields;
  }

  try {
    const profile = await fetchMyProfile();
    applyBannerProgressFromProfile(dispatch, profile);
    return profile;
  } catch {
    return fields;
  }
}

/**
 * Copy banner fields into persisted user state before leaving the questionnaire flow.
 */
export function persistOptimisticProgressToUser(dispatch, getState) {
  const state = typeof getState === 'function' ? getState() : null;
  if (!state) return;

  const optimisticPct = state.medicalQuestionnaireProgress?.optimisticCompletionPercentage;
  const optimisticFilled = state.medicalQuestionnaireProgress?.optimisticFilledSections;
  const sliceTotal = state.medicalQuestionnaireProgress?.totalSections;

  let section_progress = state.user?.section_progress;
  let sections_completed = state.user?.sections_completed;
  let total_sections = state.user?.totalSections;

  if (optimisticPct != null && Number.isFinite(Number(optimisticPct))) {
    const opt = Math.min(100, Math.max(0, Math.round(Number(optimisticPct))));
    section_progress =
      section_progress != null && Number.isFinite(Number(section_progress))
        ? Math.max(Number(section_progress), opt)
        : opt;
  }
  if (optimisticFilled != null && Number(optimisticFilled) > 0) {
    const optFilled = Math.max(0, Math.round(Number(optimisticFilled)));
    sections_completed =
      sections_completed != null && Number.isFinite(Number(sections_completed))
        ? Math.max(Number(sections_completed), optFilled)
        : optFilled;
  }
  if (
    (total_sections == null || total_sections <= 0) &&
    sliceTotal != null &&
    Number(sliceTotal) > 0
  ) {
    total_sections = Number(sliceTotal);
  }

  const payload = {};
  if (section_progress != null && Number.isFinite(Number(section_progress))) {
    payload.section_progress = Math.min(100, Math.max(0, Math.round(Number(section_progress))));
  }
  if (sections_completed != null && Number.isFinite(Number(sections_completed))) {
    payload.sections_completed = Math.max(0, Math.round(Number(sections_completed)));
  }
  if (total_sections != null && Number.isFinite(Number(total_sections))) {
    payload.total_sections = Math.max(0, Math.round(Number(total_sections)));
  }

  if (Object.keys(payload).length === 0) return;

  dispatch(
    setProfile({
      ...(payload.section_progress != null ? { section_progress: payload.section_progress } : {}),
      ...(payload.sections_completed != null
        ? { sections_completed: payload.sections_completed }
        : {}),
      ...(payload.total_sections != null ? { totalSections: payload.total_sections } : {}),
    })
  );
  persistLoginQuestionnaireFields(payload);
}

/**
 * When leaving the flow on the last optional question with no answer, POST empty and mark 100%.
 */
export async function finalizeLastOptionalQuestionnaireStep(dispatch, flowState) {
  const {
    currentStep,
    totalSteps,
    active,
    answers,
    sections,
    completedSteps = {},
    questionnaireCompleted,
    finalizeDoneRef,
  } = flowState || {};

  if (questionnaireCompleted) return;
  if (finalizeDoneRef?.current) return;
  if (!active?.optional || !Number.isFinite(active?.questionId)) return;
  if (totalSteps <= 0 || currentStep !== totalSteps - 1) return;

  if (finalizeDoneRef) finalizeDoneRef.current = true;

  try {
    const { questionnaireFields } = await saveMedicalQuestionnaireStepAnswer(answers, active, {
      markQuestionnaireComplete: true,
    });
    await applyQuestionnaireProgressAfterSave(dispatch, questionnaireFields, {
      forceComplete: true,
      refetchProfile: true,
    });
    syncOptimisticQuestionnaireProgress(dispatch, sections, answers, {
      ...completedSteps,
      [active.key]: true,
    });
  } catch {
    persistMedicalQuestionnaireCompletedOnClient();
    applyQuestionnaireFieldsFromSave(dispatch, null, { forceComplete: true });
    syncOptimisticQuestionnaireProgress(dispatch, sections, answers, {
      ...completedSteps,
      [active.key]: true,
    });
  }
}

export function flushQuestionnaireProgressOnFlowExit(dispatch, getState) {
  persistOptimisticProgressToUser(dispatch, getState);
  dispatch(clearOptimisticQuestionnaireProgress());
}

/** @deprecated Use flushQuestionnaireProgressOnFlowExit */
export function clearQuestionnaireFlowOptimisticProgress(dispatch) {
  dispatch(clearOptimisticQuestionnaireProgress());
}
